// ============================================================================
// DraftReviewCard — review a whole PQ-drafted proposal as ONE document.
// ----------------------------------------------------------------------------
// The card-per-suggestion flow (ProposalCard) is right for "add this one
// exclusion". It is wrong for a full proposal: a 31-line draft arrived as 33
// separate Apply buttons, which is not a review, it is data entry.
//
// Design rules this implements (docs/pq_draft_proposal_spec.md §6):
//
//  - THE HEADER IS THE SAFETY NET. Per-line vigilance decays; nobody audits 31
//    badges on a busy day. The counts are tappable filters, so "6 stale" is a
//    two-second triage instead of a line-by-line hunt.
//  - Badges carry a LETTER as well as a colour. Colour alone fails on a phone
//    in direct sun and fails for colour-blind users, and this gets read at
//    jobsites.
//  - Unpriced lines show an empty price field, NOT a grey badge. A blank
//    announces itself as unfinished; a grey badge gets skimmed past.
//  - Tap a badge to see the source. Expand-in-place, not a popover — the chat
//    panel is ~400px wide, where popovers are miserable at any screen size.
//
// The badge tier is computed server-side and only displayed here. See the
// spec's governing rule: PQ names the source, the server grades it.
// ============================================================================
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Plus, X, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BASIS_TIERS as TIERS, BasisBadge } from '@/components/pq/basisTiers';
import DraftReportCard from '@/components/pq/DraftReportCard';

// Tier definitions and the badge live in basisTiers.js — ONE definition
// shared with ProposalCard, so the two surfaces cannot drift apart.
const usd = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `−$${abs}` : `$${abs}`;
};

/** One priced line: description, price, quiet badge, tap-to-expand source. */
const DraftLine = ({ item }) => {
  const basis = item?.basis || null;
  const tier = basis ? TIERS[basis.tier] : null;
  // A credit line is priced too — it just prices negative.
  const priced = Number(item?.rate) !== 0;
  const credit = Number(item?.rate) < 0;

  return (
    <div className="py-1.5 border-b border-border/50 last:border-b-0">
      <div className="flex items-start gap-2">
        <span className="flex-1 min-w-0 text-[12px] text-foreground">
          {item.desc}
          {Number(item.qty) && Number(item.qty) !== 1 ? (
            <span className="text-muted-foreground">
              {' '}
              — {item.qty} {item.unit || 'ea'}
            </span>
          ) : null}
        </span>

        {priced ? (
          <span
            className={cn(
              'shrink-0 text-[12px] tabular-nums',
              credit ? 'text-green-600' : 'text-foreground'
            )}
          >
            {usd(item.rate)}
          </span>
        ) : (
          // Deliberately looks like an unfilled field, not a badge.
          <span className="shrink-0 text-[11px] text-muted-foreground border-b border-dashed border-muted-foreground/50 px-3">
            &nbsp;
          </span>
        )}

        {tier ? (
          <BasisBadge basis={basis} className="justify-self-end" />
        ) : (
          <span className="shrink-0 w-4" aria-hidden="true" />
        )}
      </div>
    </div>
  );
};

const CountChip = ({ active, onClick, count, label, cls, testId }) => {
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded border px-1.5 py-0.5 text-[10px] font-medium transition-opacity',
        cls,
        active ? 'ring-1 ring-offset-0 ring-current' : 'opacity-80 hover:opacity-100'
      )}
      data-testid={testId}
    >
      {count} {label}
    </button>
  );
};

const DraftReviewCard = ({ draft, state, onApply, onDismiss, canApply, t }) => {
  const [filter, setFilter] = useState(null);
  const sections = useMemo(
    () => (Array.isArray(draft?.sections) ? draft.sections : []),
    [draft]
  );

  // Counts drive the header, and the header drives triage.
  const counts = useMemo(() => {
    const c = { total: 0, A: 0, B: 0, C: 0, Y: 0, none: 0 };
    for (const s of sections) {
      for (const it of Array.isArray(s?.items) ? s.items : []) {
        c.total += 1;
        const tier = it?.basis?.tier;
        if (tier && c[tier] !== undefined) c[tier] += 1;
        else c.none += 1;
      }
    }
    return c;
  }, [sections]);

  // Every hook above this line — the dismissed early-return must not sit
  // between hook calls.
  if (state === 'dismissed') return null;
  const applied = state === 'applied';
  const inEstimate = state === 'in_estimate';

  const matchesFilter = (it) => {
    if (!filter) return true;
    const tier = it?.basis?.tier || null;
    return filter === 'none' ? !tier : tier === filter;
  };

  const toggle = (key) => setFilter((f) => (f === key ? null : key));

  return (
    <div
      className="mr-auto w-full rounded-lg border border-primary/40 bg-primary/5 px-3 py-2.5 space-y-2"
      data-testid={`pq-draft-${draft.id}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-primary">
        <FileText size={12} />
        <span>{t('pqchat.draft_label', { defaultValue: 'Full proposal draft' })}</span>
      </div>

      {draft.title ? (
        <div className="text-sm font-medium text-foreground">{draft.title}</div>
      ) : null}

      {/* The safety net: counts, tappable to filter. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-muted-foreground">
          {t('pqchat.draft_lines', {
            defaultValue: '{{count}} lines',
            count: counts.total,
          })}
        </span>
        <CountChip
          count={counts.A} label="sourced" cls={TIERS.A.cls} active={filter === 'A'}
          onClick={() => toggle('A')} testId="pq-draft-filter-A"
        />
        <CountChip
          count={counts.B} label="stale" cls={TIERS.B.cls} active={filter === 'B'}
          onClick={() => toggle('B')} testId="pq-draft-filter-B"
        />
        <CountChip
          count={counts.C} label="thin" cls={TIERS.C.cls} active={filter === 'C'}
          onClick={() => toggle('C')} testId="pq-draft-filter-C"
        />
        <CountChip
          count={counts.Y} label="your figure" cls={TIERS.Y.cls} active={filter === 'Y'}
          onClick={() => toggle('Y')} testId="pq-draft-filter-Y"
        />
        <CountChip
          count={counts.none} label="need a number"
          cls="bg-muted text-muted-foreground border-border"
          active={filter === 'none'} onClick={() => toggle('none')}
          testId="pq-draft-filter-none"
        />
      </div>

      <div className="max-h-80 overflow-y-auto kc-scroll rounded border border-border/60 bg-[hsl(var(--card))] px-2 py-1">
        {sections.map((section, si) => {
          const items = (Array.isArray(section?.items) ? section.items : []).filter(
            matchesFilter
          );
          const scopeItems = Array.isArray(section?.scope_items) ? section.scope_items : [];
          // While filtering, show only sections with surviving priced lines.
          if (filter && items.length === 0) return null;
          return (
            <div key={si} className="py-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-primary/80 pt-1">
                {section.name}
              </div>
              {!filter && scopeItems.length > 0 ? (
                <ul className="mt-0.5 mb-1 space-y-0.5">
                  {scopeItems.map((s, i) => (
                    <li key={i} className="text-[12px] text-muted-foreground pl-2">
                      • {s}
                    </li>
                  ))}
                </ul>
              ) : null}
              {items.map((it, i) => (
                <DraftLine key={i} item={it} />
              ))}
            </div>
          );
        })}

        {!filter && Array.isArray(draft.excluded) && draft.excluded.length > 0 ? (
          <div className="py-1">
            <div className="text-[10px] uppercase tracking-[0.14em] text-red-500/80 pt-1">
              {t('pqchat.draft_excluded', { defaultValue: 'Not included' })}
            </div>
            <ul className="mt-0.5 space-y-0.5">
              {draft.excluded.map((s, i) => (
                <li key={i} className="text-[12px] text-muted-foreground pl-2">
                  • {s}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!filter && Array.isArray(draft.payment_schedule) && draft.payment_schedule.length > 0 ? (
          <div className="py-1" data-testid="pq-draft-payments">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground pt-1">
              {t('pqchat.draft_payment_applies', {
                defaultValue: 'Payment schedule — applies on a new estimate with no prices yet',
              })}
            </div>
            <ul className="mt-0.5 space-y-0.5">
              {draft.payment_schedule.map((p, i) => {
                const off =
                  p.printed_amount != null &&
                  p.amount != null &&
                  Math.abs(Number(p.printed_amount) - Number(p.amount)) > 1;
                return (
                  <li key={i} className={cn('text-[12px] pl-2', off ? 'text-amber-600' : 'text-muted-foreground')}>
                    • {p.label} — {p.pct}%
                    {p.amount != null ? ` · ${usd(p.amount)}` : ''}
                    {p.due ? ` · ${p.due}` : ''}
                    {off ? ` (printed ${usd(p.printed_amount)})` : ''}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      {counts.none > 0 ? (
        <div className="text-[11px] text-muted-foreground">
          {t('pqchat.draft_unpriced_note', {
            defaultValue:
              '{{count}} lines had no comp on file — they apply at $0 for you to price.',
            count: counts.none,
          })}
        </div>
      ) : null}

      {/* Transfers: does the draft add up to the document it came from?
          Computed server-side from the lines; shown before Apply. */}
      {draft.reconcile ? (
        <div
          className={cn(
            'rounded border px-2 py-1.5 text-[11px]',
            draft.reconcile.ok
              ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400'
              : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400'
          )}
          data-testid="pq-draft-reconcile"
        >
          {draft.reconcile.ok
            ? t('pqchat.draft_reconciled', {
                defaultValue: 'Adds up: {{total}}{{match}}',
                total: usd(draft.reconcile.computed_total),
                match:
                  draft.reconcile.printed_total != null
                    ? t('pqchat.draft_reconciled_match', {
                        defaultValue: ' — matches the printed total',
                      })
                    : '',
              })
            : t('pqchat.draft_not_reconciled', {
                defaultValue: 'Doesn’t add up yet: the draft totals {{total}}{{printed}}. Check before applying.',
                total: usd(draft.reconcile.computed_total),
                printed:
                  draft.reconcile.printed_total != null
                    ? t('pqchat.draft_not_reconciled_printed', {
                        defaultValue: ', the document prints {{printed}}',
                        printed: usd(draft.reconcile.printed_total),
                      })
                    : '',
              })}
          {!draft.reconcile.ok && draft.reconcile.pct_ok === false ? (
            <div>
              {t('pqchat.draft_pct_off', {
                defaultValue: 'Payment percents add to {{pct}}%, not 100%.',
                pct: draft.reconcile.pct_sum,
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Read the document, then read the grade, then decide — so the card
          sits between the body and Apply. Server-computed; see §8. */}
      <DraftReportCard report={draft.report} t={t} />

      <div className="flex items-center gap-2 pt-0.5">
        {applied || inEstimate ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-600">
            <Check size={12} />
            {applied
              ? t('pqchat.applied', { defaultValue: 'Added — hit Save to keep it' })
              : t('pqchat.draft_in_estimate', {
                  defaultValue: 'Everything in this draft is already in the estimate',
                })}
          </span>
        ) : !canApply ? (
          <span className="text-[11px] text-muted-foreground">
            {t('pqchat.apply_elsewhere', {
              defaultValue: 'Open the estimate in the builder to apply drafts.',
            })}
          </span>
        ) : (
          <>
            <Button
              size="sm"
              onClick={() => onApply(draft)}
              className="h-7 px-2 bg-primary text-primary-foreground hover:bg-primary/90 text-xs"
              data-testid={`pq-draft-apply-${draft.id}`}
            >
              <Plus size={12} className="mr-1" />
              {t('pqchat.draft_apply', { defaultValue: 'Apply draft' })}
            </Button>
            <button
              type="button"
              onClick={() => onDismiss(draft)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid={`pq-draft-dismiss-${draft.id}`}
            >
              <X size={12} />
              {t('pqchat.dismiss', { defaultValue: 'Dismiss' })}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DraftReviewCard;
